import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { switchMap, map, catchError, take, finalize } from 'rxjs/operators';
import * as TicketManagementActions from './ticket-management.actions';
import { TTicketManagementActionType } from './ticket-management.models';
import { FactoryFacade } from '../factory/factory.facade';
import { AppNotificationUtilClass, MachineMaintenanceDataService } from '@monorepo/services';
import { Router } from '@angular/router';
import { TREQUEST_GET_ALL_EMPLOYEE_TICKETS } from '@monorepo/types';
import { TicketManagementFacade } from './ticket-management.facade';

@Injectable({ providedIn: 'root' })
export class TicketManagementEffects {
  constructor(
    private actions$: Actions,
    private mmDataService: MachineMaintenanceDataService,
    private factoryFacade: FactoryFacade,
    private APIHelper: AppNotificationUtilClass,
    private router: Router,
    private ticketManagementfacade: TicketManagementFacade,
  ) {}

  createNewTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTicketManagementActionType.CreateNewTicket),
      switchMap((action: TicketManagementActions.CreateNewTicket) =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, factoryId]) =>
        this.mmDataService.createNewTicket({ factoryId, ...action.payload }).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.router.navigate(['/ticket-management/machine-issue-tickets']);
            return new TicketManagementActions.CreateNewTicketSuccess();
          }),
          catchError(() => {
            return of(new TicketManagementActions.CreateNewTicketFailure());
          }),
        ),
      ),
    ),
  );

  assignTicketToMe$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTicketManagementActionType.AssignTicketToMe),
      switchMap((action: TicketManagementActions.AssignTicketToMe) =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, factoryId]) =>
        this.mmDataService.assignTicketToMe({ factoryId, ...action.payload }).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.router.navigate(['/ticket-management/machine-issue-tickets']);
            return new TicketManagementActions.AssignTicketToMeSuccess();
          }),
          catchError(() => {
            return of(new TicketManagementActions.AssignTicketToMeFailure());
          }),
        ),
      ),
    ),
  );

  updateIssue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTicketManagementActionType.UpdateIssue),
      switchMap((action: TicketManagementActions.UpdateIssue) =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        this.ticketManagementfacade.getEmployeeTicketsPagination().pipe(
          take(1),
          map((pagination) => {
            return [...args, pagination] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, factoryId, pagination]) =>
        this.mmDataService.updateIssue({ factoryId, ...action.payload }).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            //Note: Reported bucket contains both reported and re-issued tickets
            const status = action.payload.ticketStatus;
            const payload = {
              listingBucket: status,
              filters: [],
              page: pagination.pageNo,
              itemsPerPage: pagination.pageSize,
            } as TREQUEST_GET_ALL_EMPLOYEE_TICKETS;
            return new TicketManagementActions.GetAllEmployeeTickets(payload, 'TICKET_ACTION_UPDATE');
          }),
          catchError(() => {
            return of(new TicketManagementActions.UpdateIssueFailure());
          }),
        ),
      ),
    ),
  );

  reAssignMechanic$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTicketManagementActionType.ReAssignMechanic),
      switchMap((action: TicketManagementActions.ReAssignMechanic) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.reAssignMechanic({ ...action.payload }).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            //Note: Reported bucket contains both reported and re-issued tickets
            this.router.navigate(['/ticket-management/machine-issue-tickets'], {
              queryParams: { ticketStatus: 'REPORTED' },
            });
            return new TicketManagementActions.ReAssignMaintenanceMechanicSuccess();
          }),
          catchError(() => {
            return of(new TicketManagementActions.ReAssignMechanicFailure());
          }),
        ),
      ),
    ),
  );

  reIssueTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTicketManagementActionType.ReIssueTicket),
      switchMap((action: TicketManagementActions.ReIssueTicket) =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        this.ticketManagementfacade.getEmployeeTicketsPagination().pipe(
          take(1),
          map((pagination) => {
            return [...args, pagination] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, factoryId, pagination]) =>
        this.mmDataService.reIssueTicket(factoryId, action.ticketId).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            //Note: Reported bucket contains both reported and re-issued tickets
            const payload = {
              listingBucket: 'REPORTED',
              filters: [],
              page: pagination.pageNo,
              itemsPerPage: pagination.pageSize,
            } as TREQUEST_GET_ALL_EMPLOYEE_TICKETS;
            return new TicketManagementActions.GetAllEmployeeTickets(payload, 'TICKET_ACTION_UPDATE');
          }),
          catchError(() => {
            return of(new TicketManagementActions.ReIssueTicketFailure());
          }),
        ),
      ),
    ),
  );

  approveTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTicketManagementActionType.ApproveTicket),
      switchMap((action: TicketManagementActions.ApproveTicket) =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, factoryId]) =>
        this.mmDataService.approveTicket(factoryId, action.ticketId).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.router.navigate(['/ticket-management/machine-issue-tickets'], {
              queryParams: { ticketStatus: 'APPROVED', fetchReason: 'TICKET_ACTION_UPDATE' },
            });
            return new TicketManagementActions.ApproveTicketSuccess();
          }),
          catchError(() => {
            return of(new TicketManagementActions.ApproveTicketFailure());
          }),
        ),
      ),
    ),
  );

  markTicketAsCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTicketManagementActionType.MarkTicketAsCompleted),
      switchMap((action: TicketManagementActions.MarkTicketAsCompleted) =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, factoryId]) =>
        this.mmDataService.markTicketAsCompleted(factoryId, action.ticketId).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            const ticketStatus = action.isActiveMachine ? 'PENDING_APPROVAL' : 'APPROVED';
            this.router.navigate(['/ticket-management/machine-issue-tickets'], {
              queryParams: { ticketStatus },
            });
            return new TicketManagementActions.MarkTicketAsCompletedSuccess();
          }),
          catchError(() => {
            return of(new TicketManagementActions.MarkTicketAsCompletedFailure());
          }),
        ),
      ),
    ),
  );

  pickupTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTicketManagementActionType.PickupTicket),
      switchMap((action: TicketManagementActions.PickupTicket) =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, factoryId]) =>
        this.mmDataService.pickupTicket(factoryId, action.ticketId).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.router.navigate(['/ticket-management/machine-issue-tickets'], {
              queryParams: { ticketStatus: 'IN_REPAIR' },
            });
            return new TicketManagementActions.PickupTicketSuccess();
          }),
          catchError(() => {
            return of(new TicketManagementActions.PickupTicketFailure());
          }),
        ),
      ),
    ),
  );

  getAllEmployeeTickets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTicketManagementActionType.GetAllEmployeeTickets),
      switchMap((action: TicketManagementActions.GetAllEmployeeTickets) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.getAllEmployeeTickets(action.payload).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((res) => new TicketManagementActions.GetAllEmployeeTicketsSuccess(res, action.fetchReason)),
          catchError(() => {
            return of(new TicketManagementActions.GetAllEmployeeTicketsFailure());
          }),
        ),
      ),
    ),
  );

  getEmployeeTicketDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTicketManagementActionType.GetEmployeeTicketDetails),
      switchMap((action: TicketManagementActions.GetEmployeeTicketDetails) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.getEmployeeTicketDetails(action.ticketId).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((res) => new TicketManagementActions.GetEmployeeTicketDetailsSuccess(res)),
          catchError(() => {
            return of(new TicketManagementActions.GetEmployeeTicketDetailsFailure());
          }),
        ),
      ),
    ),
  );

  getMachineTicketHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTicketManagementActionType.GetMachineTicketHistory),
      switchMap((action: TicketManagementActions.GetMachineTicketHistory) =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, factoryId]) =>
        this.mmDataService.getMachineTicketHistory({ factoryId, ...action.payload }).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            return new TicketManagementActions.GetMachineTicketHistorySuccess(res);
          }),
          catchError(() => {
            return of(new TicketManagementActions.GetMachineTicketHistoryFailure());
          }),
        ),
      ),
    ),
  );

  getMachineMaintenanceHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTicketManagementActionType.GetMachineMaintenanceHistory),
      switchMap((action: TicketManagementActions.GetMachineMaintenanceHistory) =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, factoryId]) =>
        this.mmDataService.getMachineMaintenanceHistory({ factoryId, ...action.payload }).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            return new TicketManagementActions.GetMachineMaintenanceHistorySuccess(res);
          }),
          catchError(() => {
            return of(new TicketManagementActions.GetMachineMaintenanceHistoryFailure());
          }),
        ),
      ),
    ),
  );

  getEmployeeTicketSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTicketManagementActionType.GetEmployeeTicketSummary),
      switchMap((action: TicketManagementActions.GetEmployeeTicketSummary) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.getEmployeeTicketSummary({ filters: action.selectedFilters }).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((res) => new TicketManagementActions.GetEmployeeTicketSummarySuccess(res)),
          catchError(() => {
            return of(new TicketManagementActions.GetEmployeeTicketSummaryFailure());
          }),
        ),
      ),
    ),
  );

  getTicketSummaryAnalytics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTicketManagementActionType.GetTicketSummaryAnalytics),
      switchMap((action: TicketManagementActions.GetTicketSummaryAnalytics) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.getTicketSummaryAnalytics({ filters: action.selectedFilters }).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((res) => new TicketManagementActions.GetTicketSummaryAnalyticsSuccess(res)),
          catchError(() => {
            return of(new TicketManagementActions.GetTicketSummaryAnalyticsFailure());
          }),
        ),
      ),
    ),
  );

  getBreakdownReasonsAndSolution = createEffect(() =>
    this.actions$.pipe(
      ofType(TTicketManagementActionType.GetBreakdownReasonAndSolutions),
      switchMap((action: TicketManagementActions.GetBreakdownReasonAndSolutions) =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, factoryId]) =>
        this.mmDataService
          .getAllBreakdownReasonsAndSolutions({
            factorySerialNumber: action.factorySerialNumber,
            factoryId,
            machineId: action.machineId,
          })
          .pipe(
            finalize(() => {
              this.APIHelper.dismissLoadingV2(loaderEl);
            }),
            map((res) => new TicketManagementActions.GetBreakdownReasonAndSolutionsSuccess(res)),
            catchError(() => {
              return of(new TicketManagementActions.GetBreakdownReasonAndSolutionsFailure());
            }),
          ),
      ),
    ),
  );

  getFactoryShiftRevisionDetails = createEffect(() =>
    this.actions$.pipe(
      ofType(TTicketManagementActionType.GetFactoryShiftRevisionDetails),
      switchMap((action: TicketManagementActions.GetFactoryShiftRevisionDetails) =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, factoryId]) =>
        this.mmDataService.getFactoryShiftRevisionDetails({ ...action.payload, factoryId }).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((res) => new TicketManagementActions.GetFactoryShiftRevisionDetailsSuccess(res)),
          catchError(() => {
            return of(new TicketManagementActions.GetFactoryShiftRevisionDetailsFailure());
          }),
        ),
      ),
    ),
  );

  deleteShiftRevision = createEffect(() =>
    this.actions$.pipe(
      ofType(TTicketManagementActionType.DeleteShiftRevision),
      switchMap((action: TicketManagementActions.DeleteShiftRevision) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.deleteShiftRevision(action.shiftRevisionId).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.APIHelper.presentToastMM('Shift revision updated');
            return new TicketManagementActions.GetFactoryShiftRevisionDetails(action.getFactoryShiftPayload);
          }),
          catchError(() => {
            return of(new TicketManagementActions.DeleteShiftRevisionFailure());
          }),
        ),
      ),
    ),
  );

  updateShiftRevision = createEffect(() =>
    this.actions$.pipe(
      ofType(TTicketManagementActionType.UpdateShiftRevision),
      switchMap((action: TicketManagementActions.UpdateShiftRevision) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.updateShiftRevision(action.payload).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.APIHelper.presentToastMM('Shift revision updated');
            return new TicketManagementActions.GetFactoryShiftRevisionDetails(action.getFactoryShiftPayload);
          }),
          catchError(() => {
            return of(new TicketManagementActions.UpdateIssueFailure());
          }),
        ),
      ),
    ),
  );

  getAllMaintenanceTickets = createEffect(() =>
    this.actions$.pipe(
      ofType(TTicketManagementActionType.GetAllMaintenanceTickets),
      switchMap((action: TicketManagementActions.GetAllMaintenanceTickets) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.getAllMaintenanceTickets(action.payload).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            return new TicketManagementActions.GetAllMaintenanceTicketsSuccess(res, action.fetchReason);
          }),
          catchError(() => {
            return of(new TicketManagementActions.GetAllMaintenanceTicketsFailure());
          }),
        ),
      ),
    ),
  );

  createMaintenanceTicket = createEffect(() =>
    this.actions$.pipe(
      ofType(TTicketManagementActionType.CreateMaintenanceTicket),
      switchMap((action: TicketManagementActions.CreateMaintenanceTicket) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.createMaintenanceTicket(action.payload).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.router.navigate(['/ticket-management/maintenance-tickets'], {
              queryParams: { ticketStatus: 'MAINTENANCE_PENDING' },
            });
            return new TicketManagementActions.CreateMaintenanceTicketSuccess();
          }),
          catchError(() => {
            return of(new TicketManagementActions.CreateMaintenanceTicketFailure());
          }),
        ),
      ),
    ),
  );

  startMaintenanceTicket = createEffect(() =>
    this.actions$.pipe(
      ofType(TTicketManagementActionType.StartMaintenanceTicket),
      switchMap((action: TicketManagementActions.StartMaintenanceTicket) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.startMaintenanceTicket(action.payload).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.router.navigate(['/ticket-management/maintenance-tickets'], {
              queryParams: { ticketStatus: 'IN_MAINTENANCE' },
            });
            return new TicketManagementActions.StartMaintenanceTicketSuccess();
          }),
          catchError(() => {
            return of(new TicketManagementActions.StartMaintenanceTicketFailure());
          }),
        ),
      ),
    ),
  );

  completeMaintenanceTicket = createEffect(() =>
    this.actions$.pipe(
      ofType(TTicketManagementActionType.CompleteMaintenanceTicket),
      switchMap((action: TicketManagementActions.CompleteMaintenanceTicket) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.completeMaintenanceTicket(action.payload).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.router.navigate(['/ticket-management/maintenance-tickets'], {
              queryParams: { ticketStatus: 'MAINTENANCE_COMPLETED', fetchReason: 'TICKET_ACTION_UPDATE' },
            });
            return new TicketManagementActions.CompleteMaintenanceTicketSuccess();
          }),
          catchError(() => {
            return of(new TicketManagementActions.CompleteMaintenanceTicketFailure());
          }),
        ),
      ),
    ),
  );

  markChecklistCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTicketManagementActionType.MarkChecklistCompleted),
      switchMap((action: TicketManagementActions.MarkChecklistCompleted) =>
        this.ticketManagementfacade.getMaintenanceTicketsPagination().pipe(
          take(1),
          map((pagination) => {
            return [action, pagination] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, pagination]) =>
        this.mmDataService.markChecklistCompleted(action.payload).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            return new TicketManagementActions.GetAllMaintenanceTickets(
              {
                maintenanceTicketStatus: action.ticketStatus,
                filters: [],
                page: pagination.pageNo,
                itemsPerPage: pagination.pageSize,
              },
              'TICKET_ACTION_UPDATE',
            );
          }),
          catchError(() => {
            return of(new TicketManagementActions.MarkChecklistCompletedFailure());
          }),
        ),
      ),
    ),
  );

  reAssignMaintenanceMechanic$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTicketManagementActionType.ReAssignMaintenanceMechanic),
      switchMap((action: TicketManagementActions.ReAssignMaintenanceMechanic) =>
        this.ticketManagementfacade.getMaintenanceTicketsPagination().pipe(
          take(1),
          map((pagination) => {
            return [action, pagination] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, pagination]) =>
        this.mmDataService.reAssignMaintenanceMechanic(action.payload).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            return new TicketManagementActions.GetAllMaintenanceTickets(
              {
                maintenanceTicketStatus: action.currentTicketStatus,
                filters: [],
                page: pagination.pageNo,
                itemsPerPage: pagination.pageSize,
              },
              'TICKET_ACTION_UPDATE',
            );
          }),
          catchError(() => {
            return of(new TicketManagementActions.ReAssignMaintenanceMechanicFailure());
          }),
        ),
      ),
    ),
  );

  getSparepartRequestBySource = createEffect(() =>
    this.actions$.pipe(
      ofType(TTicketManagementActionType.GetSparepartRequestBySource),
      switchMap((action: TicketManagementActions.GetSparepartRequestBySource) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.getSparepartRequestBySource(action.payload).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            return new TicketManagementActions.GetSparepartRequestBySourceSuccess(res);
          }),
          catchError(() => {
            return of(new TicketManagementActions.GetSparepartRequestBySourceFailure());
          }),
        ),
      ),
    ),
  );

  cancelSparepartRequest = createEffect(() =>
    this.actions$.pipe(
      ofType(TTicketManagementActionType.CancelSparepartRequest),
      switchMap((action: TicketManagementActions.CancelSparepartRequest) =>
        this.ticketManagementfacade.getEmployeeTicketsPagination().pipe(
          take(1),
          map((pagination) => {
            return [action, pagination] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, pagination]) =>
        this.mmDataService.cancelSparepartRequest(action.requestId).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.APIHelper.presentToastMM('Cancel Request Successful');
            const payload = {
              listingBucket: 'PENDING_APPROVAL',
              filters: [],
              page: pagination.pageNo,
              itemsPerPage: pagination.pageSize,
            } as TREQUEST_GET_ALL_EMPLOYEE_TICKETS;
            return new TicketManagementActions.GetAllEmployeeTickets(payload, 'TICKET_ACTION_UPDATE');
          }),
          catchError(() => {
            return of(new TicketManagementActions.CancelSparepartRequestFailure());
          }),
        ),
      ),
    ),
  );

  getSparepartRequestReasons = createEffect(() =>
    this.actions$.pipe(
      ofType(TTicketManagementActionType.GetSparepartRequestReasons),
      switchMap((action: TicketManagementActions.GetSparepartRequestReasons) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.getSparepartRequestReasons(action.sparePartType).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((res) => new TicketManagementActions.GetSparepartRequestReasonsSuccess(res)),
          catchError(() => {
            return of(new TicketManagementActions.GetSparepartRequestReasonsFailure());
          }),
        ),
      ),
    ),
  );
}
